import React, { useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {selectPortfoliosWithSignals} from "../store/slices/portfolios.slice";
import { Card, Tooltip, Col, Row, Typography, Divider, Space, Grid, Image } from "antd";
import {CalendarRange, Rss} from 'lucide-react';
import { StepForwardOutlined} from "@ant-design/icons";
import Alert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import WidgetContainer from "../components/WidgetContainer";
import {filterUserAppsIntoCategories} from "../configs/AppsConfig";
import BuilderStatesManager from "./Builders/BuilderStatesManager";
import UpcomingEventsCard from "./Dashboard/components/UpcomingEventsCard";
import {CardTitle} from "./Dashboard/components/Extra";
import SmaDrafts from "./SmaV2/SmaDrafts";
import CardStack from "../components/CardStack";
import Masonry from '@mui/lab/Masonry';
import useCompany from "./Company/useCompany";
import {setSelectedPath} from "../store/slices/path.slice";
import {useTickerSearch} from "../modules/Search/TickerSearch/hooks";
import {loadTickerSearchList} from "../store/slices/globalResources.slice";
import MiniDashboardCalendar from "./Dashboard/components/MiniDashboardCalendar";
import NewsCarousel from "./News/NewsCarousel";
import RichCard from "../components/Cards/components/RichCard";
const {useBreakpoint} = Grid;


const SignalAlert = () => {
    const navigate = useNavigate();
    const portfolios = useSelector(selectPortfoliosWithSignals);

    if (!portfolios || portfolios.length === 0) return null;

    return (
        <div className={'pl-3'}>
            <Alert variant={'outlined'}
                     className={'cursor shadow-sm'}
                     color={'info'}
                     title={'Alert'}
                     onClick={() => navigate('/signals')}
                     sx={{padding: '9px 16px'}}
                     action={
                          <StepForwardOutlined className={'mt-1'}
                                               onClick={() => navigate('/signals')}
                                               style={{fontSize: '20px'}}
                          />
                          }
            >
                  <span style={{fontWeight: 500}} className={'px-1'}>{portfolios.length}</span>
                  Portfolios Have New Signals
            </Alert>
            <Divider/>

        </div>
  )
}


const ApplicationsCard = ({categorizedApps})=>{
    const navigate = useNavigate();
    const portfolios = useSelector(selectPortfoliosWithSignals);
  return (
     <Card bordered={false}>
         <div className={'px-6 pb-2'}>
             <CardTitle title={'My Apps'}/>
         </div>
          <div className={'px-2'}>
              <WidgetContainer widgets={categorizedApps} span={6} center={false}  />
          </div>
      </Card>
  )
}


const EventsContainer = () => {
  const navigate = useNavigate();
  const calendar = useSelector((state) => state.user.upcomingEvents);
  // Get unique symbols directly in this component
  const uniqueSymbols = useMemo(() => {
    if (!calendar?.length) return ['spy'];
    const symbols = new Set();
    calendar.forEach(x => {
      if (x?.symbol) symbols.add(x.symbol);
      if (Array.isArray(x?.data)) {
        x.data.forEach(y => y?.symbol && symbols.add(y.symbol));
      }
    });
    return Array.from(symbols);
  }, [calendar]);

  // Use ticker search index here
  const { tickers, loading } = useTickerSearch({initialSymbols: uniqueSymbols});
  const isInitialized = !loading;

  // Process events only when search index is ready
  const events = useMemo(() => {
    if (!isInitialized || !calendar?.length || !tickers) return [];

    const events = [];
    const added = new Set();

    calendar.forEach(x => {
      if (x?.type === 'etf' && Array.isArray(x?.data)) {
        x.data.forEach(y => {
          if (y?.symbol && !added.has(y.symbol)) {
            const tickerObject = tickers.find(t => t.symbol === y.symbol);
            if (tickerObject) {
              const isDiv = y.event_type === 'upcomingdividend';
              const dateField = isDiv ? 'payment_date' : 'report_date';
              const date = new Date(y.data?.[dateField]);

              events.push({
                ...tickerObject,
                start: date,
                end: date,
                title: `$${y.symbol.toUpperCase()} ${tickerObject?.name || ''}: ${y.event_type.replace('upcoming', '')}`,
                description: isDiv
                  ? `Expected Dividend: $${y.data?.dollar_value} on ${y.data?.payment_date}. Must be owned by ${y.data?.ex_date}`
                  : `Earnings Estimate: $${y.data?.estimate_dollar} on ${y.data?.report_date}`,
                type: y.event_type,
                symbol: y.symbol,
                shortDesc: isDiv
                  ? `Dividend $${y.data?.dollar_value}    Ex-Date: ${y.data?.ex_date ?? "--"}`
                  : `Earnings Est: ~$${y.data?.estimate_dollar} per share`,
              });
              added.add(y.symbol);
            }
          }
        });
      }
    });

    return events.sort((a, b) => a.start - b.start);
  }, [calendar, tickers, isInitialized]);

  return (
      <>
          <MiniDashboardCalendar events={events} />
          <Divider/>
        <UpcomingEventsCard events={events} />
      </>
  );
};



const Dashboard = ({ widgets }) => {
    const dispatch = useDispatch();
  const navigate = useNavigate();
  const categorizedApps = filterUserAppsIntoCategories(widgets.map(x=>x.key));
  const isTickerSearchReady = useSelector(state=>state.global.cache.tickerSearchList!==null);

  // const isTickerSearchReady = !loading;
  const tickerNews = useSelector((state) => state.news.tickerListNews);
  const sortedTickerNews = useMemo(()=>{
      if (!tickerNews) return [];
      return tickerNews?.length>0 && [...tickerNews].sort((a,b)=>b?.metadata?.symbols?.length-a?.metadata?.symbols?.length);
  }, [tickerNews]);
  const bp = useBreakpoint();


  useEffect(()=>{
        dispatch(loadTickerSearchList());
  }, [dispatch])

    const DynamicContainer = ()=> (
        <div className={'pt-6'}>
            <div style={{maxWidth: 500, margin: 'auto'}}>
                <div className={'px-8'}>
                    <RichCard
                        imgUrl={'https://storage.googleapis.com/finiat-web-static-resources/CardIcons/node-sphere.png'}
                        title={'Trading & CRM'}
                        text={'Connect to your trading sytem or CRM to help automate workflows and keep your clients data in sync.'}
                        className={'cursor-pointer hover:shadow-lg'}
                    />
                    <Divider/>

                    {Array.isArray(sortedTickerNews) && sortedTickerNews?.length > 0 && (
                        <div>
                            <CardTitle title={'News'} extra={<Space><Rss size={28} className={'cursor-pointer'} strokeWidth={1} onClick={()=>navigate('/news')}/></Space>}/>
                        <NewsCarousel articles={sortedTickerNews.length > 20 ? sortedTickerNews.slice(0, 20) : sortedTickerNews}/>
                        </div>
                    )}
                </div>


            </div>
        </div>
    )

    return (

        <div className="app-home hide-scrollbar h-[90vh] overflow-hidden">
            <Card bordered={false} className={'gapless h-[90vh] overflow-hidden bg-transparent hide-scrollbar p-8'}>
                <Masonry
                    columns={bp.xxl ? 3 : (bp.lg ? 2 : 1)}
                    spacing={6}
                >
                    <div>
                        <SignalAlert/>
                        <ApplicationsCard categorizedApps={categorizedApps}/>
                        {
                            !bp.xxl && (
                                <>
                                <Divider/>
                                    <DynamicContainer/>
                                </>
                            )
                        }
                    </div>
                    {bp.xxl && (
                        <DynamicContainer/>
                    )}
                    <div>
                        <Card bordered={false}>
                            {isTickerSearchReady && <EventsContainer/>}
                            <div className="max-h-[500px] overflow-y-auto hide-scrollbar">
                                <Space direction="vertical" className="w-full">
                                    <BuilderStatesManager/>
                                    <SmaDrafts/>
                                </Space>
                            </div>
                        </Card>
                    </div>
                </Masonry>
            </Card>
        </div>
    );
};

export default Dashboard;
